import { EventEmitter } from 'events';
import { ApiResponse, Scale, Printer, Weight, ServerStatus, Scanner } from '@/types/hardware';
import { PackageScanEvent } from '@/types/packageTypes';

interface HardwareServiceConfig {
  serverUrl?: string;
  reconnectInterval?: number;
  healthCheckInterval?: number;
  useUHB?: boolean; // Use Universal Hardware Bridge instead of legacy bridge
  uhbPort?: number; // UHB WebSocket port (default 3474)
}

export interface HardwareAvailability {
  isAvailable: boolean;
  serverStatus?: ServerStatus;
  error?: string;
}

export class HardwareIntegrationService extends EventEmitter {
  private config: Required<HardwareServiceConfig>;
  private isConnected: boolean = false;
  private eventSource: EventSource | null = null;
  private healthCheckTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private scales: Map<string, Scale> = new Map();
  private printers: Map<string, Printer> = new Map();
  private scanners: Map<string, Scanner> = new Map();
  private isElectron: boolean = false;
  private websocket: WebSocket | null = null;
  private uhbDevices: Map<string, any> = new Map(); // UHB device cache
  private settingsLoaded: boolean = false;
  private lastScannedData: string | null = null;
  private deviceListReceived: boolean = false;
  private pendingDiscoveryCallbacks: Array<() => void> = [];
  private pendingWeightRequests: Map<string, Promise<Weight>> = new Map();
  private scannerSubscribed: boolean = false; // Track whether we've subscribed to scanner events via UHB
  private isInitialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private lastWeightRequestTime: number = 0;
  private lastKnownWeight: Weight | null = null;
  private _initialConfig: HardwareServiceConfig = {};

  constructor(config: HardwareServiceConfig = {}) {
    super();

    // Increase max listeners to handle multiple concurrent weight requests
    this.setMaxListeners(20);

    // Detect if running in Electron
    this.isElectron = 'electron' in window && (window as any).electron?.isElectron === true;

    // Start with default config, will be updated when settings are loaded
    this.config = {
      serverUrl: config.serverUrl || '',
      reconnectInterval: config.reconnectInterval || 5000,
      healthCheckInterval: config.healthCheckInterval || (this.isElectron ? 30000 : 10000),
      useUHB: config.useUHB !== undefined ? config.useUHB : true,
      uhbPort: config.uhbPort || 3474
    };

    // Store config for deferred settings load (loaded in initialize() instead)
    this._initialConfig = config;
  }

  /**
   * Check if hardware debug logging should be enabled
   * Only logs when in development mode AND hardware widget is visible
   */
  private shouldLog(): boolean {
    if (process.env.NODE_ENV !== 'development') {
      return false;
    }

    // Check if hardware widget is visible in localStorage
    try {
      const widgetVisible = localStorage.getItem('hardware_widget_visible');
      return widgetVisible === 'true';
    } catch (error) {
      // If localStorage is not available, default to true in development
      return true;
    }
  }

  /**
   * Conditional console.log - only logs when shouldLog() returns true
   */
  private debugLog(...args: any[]): void {
    if (this.shouldLog()) {
      console.log(...args);
    }
  }

  /**
   * Conditional console.warn - only logs when shouldLog() returns true
   */
  private debugWarn(...args: any[]): void {
    if (this.shouldLog()) {
      console.warn(...args);
    }
  }

  /**
   * Conditional console.error - only logs when shouldLog() returns true
   */
  private debugError(...args: any[]): void {
    if (this.shouldLog()) {
      console.error(...args);
    }
  }

  /**
   * Load hardware integration settings from backend
   */
  private async loadSettingsFromBackend(config: HardwareServiceConfig): Promise<void> {
    try {
      // Get API URL from helper
      const { getApiUrl } = await import('@/helpers/apiUrl');
      const apiUrl = getApiUrl('/api/settings/hardware-integration');
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });

      if (response.ok) {
        const settings = await response.json();
        this.debugLog('[HardwareService] Loaded settings from backend:', settings);
        
        // Apply settings from backend, but allow config overrides
        const shouldUseUHB = config.useUHB !== undefined ? config.useUHB : settings.useUHB;
        const uhbPort = config.uhbPort || settings.uhbPort || 3474;
        const legacyPort = settings.legacyPort || 11847;
        
        let defaultUrl: string;
        if (shouldUseUHB) {
          defaultUrl = `ws://127.0.0.1:${uhbPort}/ws`;
          this.debugLog('[HardwareService] Using Universal Hardware Bridge on port', uhbPort);
        } else {
          defaultUrl = `http://127.0.0.1:${legacyPort}`;
          this.debugLog('[HardwareService] Using legacy hardware bridge on port', legacyPort);
        }
        
        this.config = {
          serverUrl: config.serverUrl || defaultUrl,
          reconnectInterval: config.reconnectInterval || 5000,
          healthCheckInterval: config.healthCheckInterval || (this.isElectron ? 30000 : 10000),
          useUHB: shouldUseUHB,
          uhbPort: uhbPort
        };
        
        this.settingsLoaded = true;
        this.emit('settings-loaded', this.config);
      } else {
        this.debugWarn('[HardwareService] Failed to load settings from backend, using defaults');
        this.applyDefaultSettings(config);
      }
    } catch (error) {
      this.debugError('[HardwareService] Error loading settings from backend:', error);
      this.applyDefaultSettings(config);
    }
  }

  /**
   * Apply default settings when backend is not available
   */
  private applyDefaultSettings(config: HardwareServiceConfig): void {
    // Check if we should use UHB based on environment or config
    const shouldUseUHB = config.useUHB !== undefined ? config.useUHB : 
                        process.env.VUE_APP_USE_UHB === 'true' || 
                        process.env.VUE_APP_HARDWARE_MODE === 'uhb' ||
                        !process.env.VUE_APP_HARDWARE_PORT; // Default to UHB if no legacy port
    
    this.debugLog('[HardwareService] Using default configuration:', {
      configUseUHB: config.useUHB,
      envUseUHB: process.env.VUE_APP_USE_UHB,
      envHardwareMode: process.env.VUE_APP_HARDWARE_MODE,
      shouldUseUHB
    });
    
    let defaultUrl: string;
    
    if (shouldUseUHB) {
      // Use UHB WebSocket connection
      const uhbPort = config.uhbPort || parseInt(process.env.VUE_APP_UHB_PORT || '3474');
      defaultUrl = `ws://127.0.0.1:${uhbPort}/ws`;
      this.debugLog('[HardwareService] Using Universal Hardware Bridge on port', uhbPort);
    } else {
      // Use legacy TL-OES hardware bridge
      const hardwarePort = process.env.VUE_APP_HARDWARE_PORT || '11847';
      defaultUrl = `http://127.0.0.1:${hardwarePort}`;
      
      // For production Azure deployments, we need to handle HTTPS->HTTP
      if (window.location.protocol === 'https:' && !window.location.hostname.includes('localhost')) {
        this.debugLog('[HardwareService] Production HTTPS detected. Hardware bridge on local port', hardwarePort);
        defaultUrl = `http://127.0.0.1:${hardwarePort}`;
      }
    }
    
    this.config = {
      serverUrl: config.serverUrl || defaultUrl,
      reconnectInterval: config.reconnectInterval || 5000,
      healthCheckInterval: config.healthCheckInterval || (this.isElectron ? 30000 : 10000),
      useUHB: shouldUseUHB,
      uhbPort: config.uhbPort || 3474
    };
    
    this.settingsLoaded = true;
    this.emit('settings-loaded', this.config);
  }

  /**
   * Initialize the service and start monitoring
   */
  async initialize(): Promise<void> {
    // Return existing initialization promise if already initializing
    if (this.initializationPromise) {
      this.debugLog('[HardwareService] Initialization already in progress, waiting...');
      return this.initializationPromise;
    }

    // Return immediately if already initialized
    if (this.isInitialized) {
      this.debugLog('[HardwareService] Already initialized');
      return;
    }

    // Create and store initialization promise to prevent concurrent initializations
    this.initializationPromise = this.performInitialization();

    try {
      await this.initializationPromise;
      this.isInitialized = true;
      this.debugLog('[HardwareService] Initialization completed');
    } finally {
      this.initializationPromise = null;
    }
  }

  /**
   * Perform the actual initialization
   */
  private async performInitialization(): Promise<void> {
    this.debugLog('[HardwareService] Starting initialization...');

    // Load settings from backend (deferred from constructor to avoid
    // unnecessary API calls when the service is created but not used)
    if (!this.settingsLoaded) {
      await this.loadSettingsFromBackend(this._initialConfig);
    }

    if (this.config.useUHB) {
      // For UHB, connect via WebSocket
      await this.connectToUHB();
    } else {
      // Legacy hardware bridge
      await this.checkAvailability();

      // In Electron, skip event streams to prevent freezing
      // but still allow basic HTTP operations
      if (this.isElectron) {
        this.debugLog('[HardwareService] Running in Electron - skipping event streams');
        // Only do periodic health checks, no continuous streams
        this.startHealthCheck();
      } else {
        // Normal browser mode - use all features
        this.startHealthCheck();
        this.connectToEventStreams();
      }
    }
  }

  /**
   * Check if the hardware integration server is available
   */
  async checkAvailability(): Promise<HardwareAvailability> {
    if (this.config.useUHB) {
      // For UHB, we need to establish WebSocket connection first
      // Return a pending state - the actual connection will happen in initialize()
      return {
        isAvailable: true, // Assume available, WebSocket will connect async
        serverStatus: {
          running: true,
          version: 'UHB-1.0.0',
          connectedScales: this.scales.size,
          connectedPrinters: this.printers.size,
          uptime: 0
        }
      };
    }
    
    if (process.env.NODE_ENV === 'development') {
      this.debugLog('[HardwareService] Checking availability at:', this.config.serverUrl);
    }
    
    try {
      const response = await fetch(`${this.config.serverUrl}/health`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (process.env.NODE_ENV === 'development') {
        this.debugLog('[HardwareService] Response status:', response.status);
      }

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const data: ApiResponse<ServerStatus> = await response.json();
      
      if (data.success && data.data) {
        this.isConnected = true;
        this.emit('connected', data.data);
        if (process.env.NODE_ENV === 'development') {
          this.debugLog('[HardwareService] Hardware server is available');
        }
        return {
          isAvailable: true,
          serverStatus: data.data
        };
      }

      return {
        isAvailable: false,
        error: 'Invalid server response'
      };
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        this.debugError('[HardwareService] Error checking availability:', error);
      }
      this.isConnected = false;
      this.emit('disconnected', error);
      return {
        isAvailable: false,
        error: error.message || 'Hardware integration server not available'
      };
    }
  }

  /**
   * Discover available scales
   */
  async discoverScales(): Promise<Scale[]> {
    if (this.config.useUHB) {
      return this.discoverScalesUHB();
    }
    
    const response = await this.makeRequest<Scale[]>('/api/scales/discover');
    if (response.success && response.data) {
      // Update internal cache
      this.scales.clear();
      response.data.forEach(scale => {
        this.scales.set(scale.id, scale);
      });
      return response.data;
    }
    throw new Error(response.error || 'Failed to discover scales');
  }

  /**
   * Connect to a specific scale
   */
  async connectToScale(scaleId: string): Promise<void> {
    if (this.config.useUHB) {
      return this.connectToScaleUHB(scaleId);
    }
    
    const response = await this.makeRequest('/api/scales/connect', {
      method: 'POST',
      body: JSON.stringify({ scaleId })
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to connect to scale');
    }

    // Update scale status
    const scale = this.scales.get(scaleId);
    if (scale) {
      scale.isConnected = true;
    }
  }

  /**
   * Get current weight from a scale
   */
  async getWeight(scaleId: string): Promise<Weight> {
    this.debugLog(`[HardwareService] getWeight called for scale: ${scaleId}`);
    if (this.config.useUHB) {
      return this.getWeightUHB(scaleId);
    }

    const response = await this.makeRequest<Weight>(`/api/scales/weight/${scaleId}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to get weight');
  }

  /**
   * Disconnect from a scale
   */
  async disconnectScale(scaleId: string): Promise<void> {
    const response = await this.makeRequest(`/api/scales/disconnect/${scaleId}`, {
      method: 'POST'
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to disconnect scale');
    }

    // Update scale status
    const scale = this.scales.get(scaleId);
    if (scale) {
      scale.isConnected = false;
    }
  }

  /**
   * Get connected scales
   */
  async getConnectedScales(): Promise<Scale[]> {
    const response = await this.makeRequest<Scale[]>('/api/scales/status');
    if (response.success && response.data) {
      return response.data;
    }
    return [];
  }

  /**
   * Discover available printers
   */
  async discoverPrinters(): Promise<Printer[]> {
    if (this.config.useUHB) {
      this.debugLog('[HardwareService] Discovering printers in UHB mode');
      
      // If device list already received, return cached printers immediately
      if (this.deviceListReceived) {
        const printers = Array.from(this.printers.values());
        this.debugLog('[HardwareService] Returning cached printers (device list already received):', printers.length);
        return printers;
      }
      
      // If WebSocket not connected, wait for connection
      if (!this.isConnected || this.websocket?.readyState !== WebSocket.OPEN) {
        this.debugLog('[HardwareService] WebSocket not ready, waiting for connection...');
        try {
          await this.waitForConnection();
        } catch (error) {
          this.debugWarn('[HardwareService] Connection wait failed, proceeding anyway:', error);
          // Return empty array if connection fails
          return [];
        }
      }
      
      // Request device list and wait for response
      return new Promise<Printer[]>((resolve) => {
        let resolved = false;
        
        // Add callback to pending list
        const callback = () => {
          if (resolved) return;
          resolved = true;
          const printers = Array.from(this.printers.values());
          this.debugLog('[HardwareService] Resolving pending discovery with printers:', printers.length);
          resolve(printers);
        };
        
        this.pendingDiscoveryCallbacks.push(callback);
        
        // Request device list
        this.debugLog('[HardwareService] Requesting device list from UHB...');
        this.sendUHBMessage({ type: 'device.list' });
        
        // Fallback timeout to prevent infinite waiting
        setTimeout(() => {
          if (resolved) return;
          resolved = true;
          
          // Remove this callback from pending list
          const index = this.pendingDiscoveryCallbacks.indexOf(callback);
          if (index > -1) {
            this.pendingDiscoveryCallbacks.splice(index, 1);
          }
          
          const printers = Array.from(this.printers.values());
          this.debugLog('[HardwareService] Discovery timeout, returning current cache:', printers.length);
          resolve(printers);
        }, 2000);
      });
    }
    
    const response = await this.makeRequest<Printer[]>('/api/printers/discover');
    if (response.success && response.data) {
      // Update internal cache
      this.printers.clear();
      response.data.forEach(printer => {
        this.printers.set(printer.id, printer);
      });
      return response.data;
    }
    throw new Error(response.error || 'Failed to discover printers');
  }

  /**
   * Connect to a printer
   */
  async connectToPrinter(printerId: string): Promise<void> {
    if (this.config.useUHB) {
      return this.connectToPrinterUHB(printerId);
    }
    
    const response = await this.makeRequest('/api/printers/connect', {
      method: 'POST',
      body: JSON.stringify({ printerId })
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to connect to printer');
    }

    // Update printer status
    const printer = this.printers.get(printerId);
    if (printer) {
      printer.isConnected = true;
    }
  }

  /**
   * Print a label
   */
  async printLabel(
    printerId: string,
    data: string | ArrayBuffer,
    format: 'zpl' | 'epl' | 'raw' = 'raw'
  ): Promise<string> {
    if (this.config.useUHB) {
      return this.printLabelUHB(printerId, data, format);
    }

    const response = await this.makeRequest<{ jobId: string }>('/api/printers/print', {
      method: 'POST',
      body: JSON.stringify({
        printerId,
        data: typeof data === 'string' ? data : btoa(String.fromCharCode(...new Uint8Array(data))),
        format
      })
    });

    if (response.success && response.data) {
      return response.data.jobId;
    }
    throw new Error(response.error || 'Failed to print label');
  }

  /**
   * Print a label using UHB WebSocket
   */
  private async printLabelUHB(
    printerId: string,
    data: string | ArrayBuffer,
    format: 'zpl' | 'epl' | 'raw' = 'raw'
  ): Promise<string> {
    if (!this.config.useUHB) {
      throw new Error('Not in UHB mode');
    }

    // Convert data to string if needed
    const printData = typeof data === 'string'
      ? data
      : btoa(String.fromCharCode(...new Uint8Array(data)));

    this.debugLog('[HardwareService] Starting UHB print with direct WebSocket method');
    this.debugLog('[HardwareService] Input data type:', typeof data);
    this.debugLog('[HardwareService] Input data length:', typeof data === 'string' ? data.length : data.byteLength);
    this.debugLog('[HardwareService] Format:', format);
    this.debugLog('[HardwareService] PrinterId:', printerId);
    this.debugLog('[HardwareService] Converted printData:', printData);

    // Fall back to direct WebSocket message if data URI doesn't work
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout waiting for print response'));
      }, 30000); // 30 second timeout for direct print

      // Generate unique message ID
      const messageId = `print-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Set up one-time response handler
      const handler = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data);
          if (message.id === messageId) {
            this.debugLog('[HardwareService] Received print response:', JSON.stringify(message));
            clearTimeout(timeout);

            // Remove the handler
            if (this.websocket) {
              this.websocket.removeEventListener('message', handler);
            }

            if (message.error) {
              // More robust error handling
              this.debugError('[HardwareService] Print error response:', message.error);
              const errorMessage = typeof message.error === 'string'
                ? message.error
                : (message.error?.message || message.error?.toString?.() || 'Print failed');
              reject(new Error(errorMessage));
            } else {
              // Return a job ID for consistency
              const jobId = message.data?.jobId || `uhb-${messageId}`;
              this.debugLog('[HardwareService] Print success, job ID:', jobId);
              resolve(jobId);
            }
          }
        } catch (error) {
          this.debugError('[HardwareService] Error parsing print response:', error);
          // Ignore parse errors for messages that aren't for us
        }
      };

      // Add the handler
      if (this.websocket) {
        this.websocket.addEventListener('message', handler);
      }

      // Send raw printing message to UHB
      const printMessage = {
        id: messageId,
        type: 'printer.printRaw',
        data: {
          deviceId: printerId,
          rawData: printData,
          dataType: format
        }
      };
      this.debugLog('[HardwareService] Sending print message:', JSON.stringify({
        ...printMessage,
        data: { ...printMessage.data, rawData: `<${printData.length} chars>` }
      }));
      this.sendUHBMessage(printMessage);
    });
  }

  /**
   * Print a PDF label from URL
   * @param printerId - The ID of the printer to use
   * @param url - The URL of the PDF to print
   * @param options - Optional print settings
   */
  async printLabelFromUrl(
    printerId: string,
    url: string,
    options?: { printAllPages?: boolean; copies?: number }
  ): Promise<{ success: boolean; error?: string }> {
    if (this.config.useUHB) {
      return this.printLabelFromUrlUHB(printerId, url, options);
    }

    // Legacy hardware bridge doesn't support URL printing
    throw new Error('PDF URL printing is not supported in legacy hardware bridge mode');
  }

  /**
   * Get connected printers
   */
  async getConnectedPrinters(): Promise<Printer[]> {
    const response = await this.makeRequest<Printer[]>('/api/printers/status');
    if (response.success && response.data) {
      return response.data;
    }
    return [];
  }

  /**
   * Discover available scanners
   */
  async discoverScanners(): Promise<Scanner[]> {
    if (this.config.useUHB) {
      this.debugLog('[HardwareService] Discovering scanners in UHB mode');
      // Request device list via WebSocket
      this.sendUHBMessage({ type: 'device.list' });
      
      // Wait a bit for the device list to be received
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Return current scanners from cache
      const scanners = Array.from(this.scanners.values());
      this.debugLog('[HardwareService] Returning scanners from cache:', scanners);
      return scanners;
    }
    
    // Legacy mode doesn't support scanners
    return [];
  }

  /**
   * Connect to a scanner
   */
  async connectToScanner(scannerId: string): Promise<void> {
    if (!this.config.useUHB) {
      throw new Error('Scanner support requires Universal Hardware Bridge');
    }
    
    console.log('[HardwareService] Connecting to scanner:', scannerId);

    // Optimistically update the scanner status
    const scanner = this.scanners.get(scannerId);
    if (scanner) {
      scanner.isConnected = true;
      // Emit scanner-connected event immediately for UI update
      this.emit('scanner-connected', scanner);
    }

    this.sendUHBMessage({
      type: 'device.connect',
      data: { deviceId: scannerId }
    });

    // Subscribe to scanner events with multiple formats
    setTimeout(() => {
      console.log('[HardwareService] Subscribing to scanner events for focus-independent scanning:', scannerId);
      
      // Try multiple subscription formats
      this.sendUHBMessage({
        type: 'scanner.subscribe',
        data: { deviceId: scannerId }
      });
      
      // Alternative format 1
      this.sendUHBMessage({
        type: 'subscribe',
        data: { 
          deviceType: 'scanner',
          deviceId: scannerId,
          events: ['barcode', 'scan', 'data']
        }
      });
      
      // Alternative format 2
      this.sendUHBMessage({
        type: 'device.subscribe',
        data: { 
          deviceId: scannerId,
          eventTypes: ['scanner.data', 'scanner.barcode']
        }
      });
    }, 500);
  }

  /**
   * Disconnect from a scale
   */
  async disconnectFromScale(scaleId: string): Promise<void> {
    if (this.config.useUHB) {
      return this.disconnectFromScaleUHB(scaleId);
    }

    const response = await this.makeRequest('/api/scales/disconnect', {
      method: 'POST',
      body: JSON.stringify({ scaleId })
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to disconnect from scale');
    }

    // Update scale status
    const scale = this.scales.get(scaleId);
    if (scale) {
      scale.isConnected = false;
      this.emit('scale-disconnected', scaleId);
    }
  }

  /**
   * Disconnect from a printer
   */
  async disconnectFromPrinter(printerId: string): Promise<void> {
    if (this.config.useUHB) {
      return this.disconnectFromPrinterUHB(printerId);
    }

    const response = await this.makeRequest('/api/printers/disconnect', {
      method: 'POST',
      body: JSON.stringify({ printerId })
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to disconnect from printer');
    }

    // Update printer status
    const printer = this.printers.get(printerId);
    if (printer) {
      printer.isConnected = false;
      this.emit('printer-disconnected', printerId);
    }
  }

  /**
   * Disconnect from a scanner
   */
  async disconnectFromScanner(scannerId: string): Promise<void> {
    if (!this.config.useUHB) {
      throw new Error('Scanner support requires Universal Hardware Bridge');
    }

    this.debugLog('[HardwareService] Disconnecting from scanner:', scannerId);

    // Update the scanner status
    const scanner = this.scanners.get(scannerId);
    if (scanner) {
      scanner.isConnected = false;
      // Emit scanner-disconnected event for UI update
      this.emit('scanner-disconnected', scannerId);
    }

    const disconnectMessage = {
      type: 'device.disconnect',
      data: { deviceId: scannerId }
    };

    this.debugLog('[HardwareService] Sending UHB disconnect message:', disconnectMessage);
    this.sendUHBMessage(disconnectMessage);
  }

  /**
   * Disconnect from a scanner (legacy method name for compatibility)
   */
  async disconnectScanner(scannerId: string): Promise<void> {
    return this.disconnectFromScanner(scannerId);
  }

  /**
   * Start scanning mode (for mobile camera)
   */
  async startCameraScanning(): Promise<void> {
    if (this.config.useUHB) {
      this.sendUHBMessage({
        type: 'scanner.camera.start'
      });
    }
    this.emit('scanner-camera-started');
  }

  /**
   * Stop camera scanning
   */
  async stopCameraScanning(): Promise<void> {
    if (this.config.useUHB) {
      this.sendUHBMessage({
        type: 'scanner.camera.stop'
      });
    }
    this.emit('scanner-camera-stopped');
  }

  /**
   * Get last scanned data
   */
  getLastScannedData(): string | null {
    return this.lastScannedData;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopHealthCheck();
    this.disconnectEventStreams();
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.removeAllListeners();
  }

  private async makeRequest<T = any>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    // Allow health checks and scale operations without connection check
    // since scales can work even if event streams aren't connected
    const allowedWithoutConnection = ['/health', '/api/scales/', '/api/printers/'];
    const isAllowed = allowedWithoutConnection.some(path => endpoint.includes(path));
    
    if (!this.isConnected && !isAllowed) {
      throw new Error('Hardware integration server not connected');
    }

    try {
      const response = await fetch(`${this.config.serverUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        }
      });

      const data: ApiResponse<T> = await response.json();
      return data;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Request failed',
        timestamp: Date.now()
      };
    }
  }

  private connectToEventStreams(): void {
    if (!this.isConnected) return;

    // Connect to scale events
    this.connectToScaleEvents();
    
    // Connect to printer events
    this.connectToPrinterEvents();
  }

  private connectToScaleEvents(): void {
    try {
      // Add a connection timeout
      const connectionTimeout = setTimeout(() => {
        this.debugWarn('[HardwareService] EventSource connection timeout');
        if (this.eventSource) {
          this.eventSource.close();
          this.eventSource = null;
        }
      }, 5000); // 5 second timeout
      
      const scaleEventSource = new EventSource(`${this.config.serverUrl}/api/events/scales`);
      
      scaleEventSource.onopen = () => {
        clearTimeout(connectionTimeout);
        this.debugLog('[HardwareService] Scale event stream connected');
      };
      
      scaleEventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'weight':
              this.emit('weight', data.data);
              break;
            case 'connected':
              this.emit('scale-connected', data.data);
              break;
            case 'disconnected':
              this.emit('scale-disconnected', data.data);
              break;
            case 'error':
              this.emit('scale-error', data.data);
              break;
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            this.debugError('Failed to parse scale event:', error);
          }
        }
      };

      scaleEventSource.onerror = (error) => {
        clearTimeout(connectionTimeout);
        if (process.env.NODE_ENV === 'development') {
          this.debugError('Scale event stream error:', error);
        }
        scaleEventSource.close();
        this.scheduleReconnect();
      };

      this.eventSource = scaleEventSource;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        this.debugError('Failed to connect to scale events:', error);
      }
    }
  }

  private connectToPrinterEvents(): void {
    try {
      const printerEventSource = new EventSource(`${this.config.serverUrl}/api/events/printers`);
      
      printerEventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'printer-event' && data.data) {
            this.emit('printer-event', data.data);
            
            // Emit specific events
            switch (data.data.type) {
              case 'job_started':
                this.emit('print-started', data.data);
                break;
              case 'job_completed':
                this.emit('print-completed', data.data);
                break;
              case 'job_failed':
                this.emit('print-failed', data.data);
                break;
            }
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            this.debugError('Failed to parse printer event:', error);
          }
        }
      };

      printerEventSource.onerror = (error) => {
        if (process.env.NODE_ENV === 'development') {
          this.debugError('Printer event stream error:', error);
        }
        printerEventSource.close();
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        this.debugError('Failed to connect to printer events:', error);
      }
    }
  }

  private disconnectEventStreams(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private startHealthCheck(): void {
    this.healthCheckTimer = window.setInterval(async () => {
      const availability = await this.checkAvailability();
      if (!availability.isAvailable && this.isConnected) {
        this.isConnected = false;
        this.disconnectEventStreams();
        this.scheduleReconnect();
      }
    }, this.config.healthCheckInterval);
  }

  private stopHealthCheck(): void {
    if (this.healthCheckTimer) {
      window.clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    this.reconnectTimer = window.setTimeout(async () => {
      this.reconnectTimer = null;
      if (this.config.useUHB) {
        await this.connectToUHB();
      } else {
        const availability = await this.checkAvailability();
        if (availability.isAvailable) {
          this.connectToEventStreams();
        } else {
          this.scheduleReconnect();
        }
      }
    }, this.config.reconnectInterval);
  }

  // ==================== UHB WebSocket Methods ====================

  /**
   * Connect to Universal Hardware Bridge via WebSocket
   */
  private async connectToUHB(): Promise<void> {
    if (this.websocket?.readyState === WebSocket.OPEN) {
      this.debugLog('[HardwareService] UHB WebSocket already connected');
      return; // Already connected
    }

    // Close any existing WebSocket that might be in a bad state
    if (this.websocket) {
      this.debugLog('[HardwareService] Closing existing WebSocket before creating new connection');
      this.websocket.close();
      this.websocket = null;
    }

    try {
      this.debugLog('[HardwareService] Connecting to UHB at:', this.config.serverUrl);

      this.websocket = new WebSocket(this.config.serverUrl);
      
      this.websocket.onopen = () => {
        console.log('[HardwareService] UHB WebSocket connected to', this.config.serverUrl);
        this.isConnected = true;
        this.emit('connected', {
          running: true,
          version: 'UHB-1.0.0',
          connectedScales: 0,
          connectedPrinters: 0,
          uptime: 0
        });
        
        // Request device list
        this.sendUHBMessage({ type: 'device.list' });
      };
      
      this.websocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleUHBMessage(message);
        } catch (error) {
          this.debugError('[HardwareService] Failed to parse UHB message:', error);
        }
      };
      
      this.websocket.onerror = (error) => {
        this.debugError('[HardwareService] UHB WebSocket error:', error);
        this.hardwareError = 'Connection error';
      };
      
      this.websocket.onclose = () => {
        console.warn('[HardwareService] UHB WebSocket disconnected');
        this.isConnected = false;
        this.deviceListReceived = false; // Reset device list status
        this.scannerSubscribed = false; // Reset so we re-subscribe on reconnect
        this.emit('disconnected', 'WebSocket closed');
        this.scheduleReconnect();
      };
      
    } catch (error: any) {
      this.debugError('[HardwareService] Failed to connect to UHB:', error);
      this.isConnected = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Send message to UHB
   */
  private sendUHBMessage(message: any): void {
    if (this.websocket?.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(message));
    }
  }

  /**
   * Handle incoming UHB messages
   */
  private handleUHBMessage(message: any): void {
    // Enhanced logging to capture all message types
    this.debugLog('[HardwareService] Raw UHB message:', JSON.stringify(message));
    this.debugLog('[HardwareService] Message type:', message.type, 'Has data:', !!message.data);

    // Handle response messages with IDs (direct responses to requests)
    if (message.id) {
      this.debugLog('[HardwareService] Received response for message ID:', message.id);
      this.emit('uhb-response', message);
    }

    // CRITICAL FIX: Check for scan data in various formats, but EXCLUDE scale weight readings
    // Scale readings have message.type === 'scale.read' and should NOT be treated as scan data
    let scanValue = null;

    // Skip scan extraction for message types that the switch case handles directly
    const knownScannerTypes = ['scale.read', 'scale.weight', 'scale.data',
      'scanner.data', 'scanner.barcode', 'barcode', 'scan', 'scanner-data'];

    if (!knownScannerTypes.includes(message.type)) {
      scanValue = message.barcode ||
                  message.data?.barcode ||
                  message.data?.data ||
                  message.value ||
                  message.scanData ||
                  message.code;

      // Only include message.data.value if it's NOT a scale reading
      if (!scanValue && message.data?.value && typeof message.data.value === 'string') {
        scanValue = message.data.value;
      }
    }

    if (scanValue) {
      this.debugLog('[HardwareService] ✅ Found valid scan value in message:', scanValue);
      this.handleUHBScannerData({ value: scanValue, raw: message });
    }
    
    switch (message.type) {
      case 'device.list':
        this.updateUHBDevices(message.data);
        break;
        
      case 'device.connected':
        // Handle both full device object and deviceId reference
        if (message.data) {
          this.handleUHBDeviceConnected(message.data);
        } else {
          this.debugWarn('[HardwareService] Invalid device.connected message data:', message.data);
        }
        break;
        
      case 'device.disconnected': {
        // Handle both string deviceId and object with deviceId property
        const disconnectedDeviceId = typeof message.data === 'string' ? message.data : message.data?.deviceId;
        if (disconnectedDeviceId) {
          this.handleUHBDeviceDisconnected(disconnectedDeviceId);
        } else {
          this.debugWarn('[HardwareService] Invalid device.disconnected message data:', message.data);
        }
        break;
      }
        
      case 'scale.data':
        this.handleUHBScaleData(message.data);
        break;
        
      case 'scale.weight':
        // Handle continuous weight updates
        this.handleUHBScaleData(message.data);
        break;
        
      case 'scanner.data':
      case 'scanner.barcode':
      case 'barcode':
      case 'scan':
      case 'scanner-data':
        console.log('[HardwareService] Scanner event received via UHB WebSocket:', message.type);
        this.handleUHBScannerData(message.data || message);
        break;
        
      case 'scanner.connected':
        this.handleUHBScannerConnected(message.data);
        break;
        
      case 'scanner.disconnected':
        this.handleUHBScannerDisconnected(message.data);
        break;
        
      case 'error':
        this.debugError('[HardwareService] UHB error:', message.error);
        this.emit('error', message.error);
        break;
        
      default:
        // Log all unknown message types for debugging
        this.debugLog('[HardwareService] Unknown message type:', message.type, 'Full message:', message);
        
        // Still try to detect scan data
        if (scanValue) {
          this.debugLog('[HardwareService] Processing scan data from unknown message type');
        } else {
          this.debugLog('[HardwareService] Unknown message type:', message.type, message);
        }
    }
  }

  /**
   * Wait for WebSocket connection to be established
   */
  private async waitForConnection(timeout: number = 5000): Promise<void> {
    if (this.isConnected && this.websocket?.readyState === WebSocket.OPEN) {
      return;
    }
    
    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, timeout);
      
      const checkConnection = () => {
        if (this.isConnected && this.websocket?.readyState === WebSocket.OPEN) {
          clearTimeout(timeoutId);
          resolve();
        } else {
          setTimeout(checkConnection, 100);
        }
      };
      
      checkConnection();
    });
  }

  /**
   * Update device list from UHB
   */
  private updateUHBDevices(devices: any[]): void {
    this.debugLog('[HardwareService] Received device list from UHB:', devices);

    // Mark that device list has been received
    this.deviceListReceived = true;

    // Don't clear existing devices - merge instead to prevent UI flicker
    // Track which devices are in the new list
    const newDeviceIds = new Set(devices.map(d => d.id));

    // Remove devices that are no longer present
    for (const deviceId of this.uhbDevices.keys()) {
      if (!newDeviceIds.has(deviceId)) {
        this.uhbDevices.delete(deviceId);
        this.scales.delete(deviceId);
        this.printers.delete(deviceId);
        this.scanners.delete(deviceId);
        this.debugLog('[HardwareService] Removed device:', deviceId);
      }
    }
    
    const hasConnectedScale = false; // For UHB, scales are never "connected" until we explicitly connect
    let firstAvailableScale: Scale | null = null;
    
    devices.forEach(device => {
      this.debugLog('[HardwareService] Processing device:', device);
      this.uhbDevices.set(device.id, device);
      
      if (device.type === 'scale') {
        const existingScale = this.scales.get(device.id);
        const scale: Scale = {
          id: device.id,
          vendorId: device.vendorId,
          productId: device.productId,
          manufacturer: device.manufacturer || 'Unknown',
          product: device.model,
          serialNumber: device.serialNumber,
          isConnected: existingScale?.isConnected ?? device.isConnected, // Preserve existing connection state or use UHB state
          type: 'usb'
        };
        this.scales.set(device.id, scale);
        this.debugLog(`[HardwareService] ${existingScale ? 'Updated' : 'Added'} scale:`, scale);
        
        // Track connection status - for UHB, no scale is "connected" until we explicitly connect
        // device.isConnected just means it's plugged into USB, not that we're streaming data
        if (!firstAvailableScale) {
          firstAvailableScale = scale;
        }
      } else if (device.type === 'printer') {
        const existingPrinter = this.printers.get(device.id);
        const printer: Printer = {
          id: device.id,
          name: device.name || device.model || 'Unknown Printer',
          model: device.model || 'Unknown Model',
          type: 'usb',
          isConnected: existingPrinter?.isConnected ?? device.isConnected, // Preserve existing connection state or use UHB state
          capabilities: {
            formats: ['raw', 'zpl', 'epl', 'tspl'],
            labelSizes: ['4x6', '4x8', '4x3', '4x2', '2x1']
          }
        };
        this.printers.set(device.id, printer);
        this.debugLog(`[HardwareService] ${existingPrinter ? 'Updated' : 'Added'} printer:`, printer);
      } else if (device.type === 'scanner' || device.type === 'barcode-scanner') {
        const existingScanner = this.scanners.get(device.id);
        const scanner: Scanner = {
          id: device.id,
          name: device.name || device.model || 'Unknown Scanner',
          model: device.model || 'Unknown Model',
          type: 'usb',
          isConnected: existingScanner?.isConnected ?? device.isConnected, // Preserve existing connection state or use UHB state
          capabilities: {
            formats: ['qr', 'barcode', 'datamatrix'],
            modes: ['trigger', 'continuous']
          }
        };
        this.scanners.set(device.id, scanner);
        this.debugLog(`[HardwareService] ${existingScanner ? 'Updated' : 'Added'} scanner:`, scanner);
      } else {
        this.debugLog('[HardwareService] Unknown device type:', device.type, 'for device:', device);
      }
    });
    
    // Emit device update
    this.emit('devices-updated', {
      fullRefresh: true, // UHB device list is always a full refresh
      scales: Array.from(this.scales.values()),
      printers: Array.from(this.printers.values()),
      scanners: Array.from(this.scanners.values())
    });
    
    // Auto-connect to first available scale if none connected
    if (!hasConnectedScale && firstAvailableScale !== null) {
      const scale: Scale = firstAvailableScale; // Help TypeScript understand the type
      this.debugLog('[HardwareService] Auto-connecting to scale:', scale.manufacturer, scale.product, 'ID:', scale.id);
      const scaleId = scale.id;
      setTimeout(() => {
        this.debugLog('[HardwareService] Executing auto-connection for scale:', scaleId);
        this.connectToScaleUHB(scaleId);
      }, 1000); // Small delay to ensure UI is ready
    } else {
      this.debugLog('[HardwareService] Scale auto-connection skipped:', { hasConnectedScale, firstAvailableScale });
    }

    // Auto-subscribe to the actual barcode scanner for focus-independent scanning via UHB.
    // UHB reports keyboards (e.g. Keychron) as "scanners" too, so we must prefer
    // actual barcode scanner devices (Symbol, Zebra, Honeywell, etc.) over keyboards.
    const scannerArray = Array.from(this.scanners.values());
    if (!this.scannerSubscribed && scannerArray.length > 0) {
      // Prefer actual barcode scanners over keyboards classified as scanners
      const barcodeScannerKeywords = ['symbol', 'zebra', 'honeywell', 'datalogic', 'barcode', 'scanner', 'motorola'];
      const keyboardKeywords = ['keychron', 'keyboard', 'logitech', 'corsair', 'razer', 'ducky'];

      const actualScanner = scannerArray.find(s => {
        const name = (s.name || s.model || '').toLowerCase();
        return barcodeScannerKeywords.some(kw => name.includes(kw));
      }) || scannerArray.find(s => {
        const name = (s.name || s.model || '').toLowerCase();
        return !keyboardKeywords.some(kw => name.includes(kw));
      }) || scannerArray[0]; // Last resort: first device

      const scannerId = actualScanner.id;
      console.log('[HardwareService] Auto-subscribing to barcode scanner:', actualScanner.name, 'ID:', scannerId,
        '(selected from', scannerArray.length, 'scanner devices)');
      this.scannerSubscribed = true;
      setTimeout(() => {
        console.log('[HardwareService] Executing auto-subscription for scanner:', scannerId);
        this.connectToScanner(scannerId);
      }, 1500);
    } else if (scannerArray.length === 0) {
      this.debugLog('[HardwareService] No scanners found in device list');
    } else {
      this.debugLog('[HardwareService] Scanner already subscribed, skipping auto-subscribe');
    }
    
    // Resolve any pending discovery callbacks
    const callbacks = [...this.pendingDiscoveryCallbacks];
    this.pendingDiscoveryCallbacks.length = 0; // Clear the array
    this.debugLog('[HardwareService] Resolving', callbacks.length, 'pending discovery callbacks');
    callbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        this.debugError('[HardwareService] Error in discovery callback:', error);
      }
    });
  }

  /**
   * Handle UHB device connected event
   */
  private handleUHBDeviceConnected(device: any): void {
    this.debugLog('[HardwareService] Device connected:', device);
    this.uhbDevices.set(device.id, device);
    
    if (device.type === 'scale') {
      const scale = this.scales.get(device.id);
      if (scale) {
        scale.isConnected = true;
        this.emit('scale-connected', scale);
        
        // Request initial reading then subscribe
        setTimeout(() => {
          // First, request a single read to "wake up" the scale
          this.sendUHBMessage({
            type: 'scale.read',
            data: { deviceId: device.id }
          });
          
          // Then subscribe for continuous updates
          setTimeout(() => {
            this.sendUHBMessage({
              type: 'scale.subscribe',
              data: { deviceId: device.id }
            });
          }, 200);
        }, 500);
      }
    } else if (device.type === 'printer') {
      const printer = this.printers.get(device.id);
      if (printer) {
        printer.isConnected = true;
        this.emit('printer-connected', printer);
      }
    } else if (device.type === 'scanner') {
      const scanner = this.scanners.get(device.id);
      if (scanner) {
        scanner.isConnected = true;
        this.debugLog('[HardwareService] Scanner connected via device.connected:', device.id);
        this.emit('scanner-connected', scanner);
        
        // Auto-subscribe to scanner events
        setTimeout(() => {
          this.debugLog('[HardwareService] Auto-subscribing to scanner:', device.id);
          this.sendUHBMessage({
            type: 'scanner.subscribe',
            data: { deviceId: device.id }
          });
        }, 500);
      }
    }
  }

  /**
   * Handle UHB device disconnected event
   */
  private handleUHBDeviceDisconnected(deviceId: string): void {
    this.debugLog('[HardwareService] Device disconnected:', deviceId);
    const device = this.uhbDevices.get(deviceId);
    if (device) {
      device.isConnected = false;

      if (device.type === 'scale') {
        const scale = this.scales.get(deviceId);
        if (scale) {
          scale.isConnected = false;
        }
        this.debugLog('[HardwareService] Emitting scale-disconnected for:', deviceId);
        this.emit('scale-disconnected', deviceId);
      } else if (device.type === 'printer') {
        const printer = this.printers.get(deviceId);
        if (printer) {
          printer.isConnected = false;
        }
        this.debugLog('[HardwareService] Emitting printer-disconnected for:', deviceId);
        this.emit('printer-disconnected', deviceId);
      } else if (device.type === 'scanner' || device.type === 'barcode-scanner') {
        const scanner = this.scanners.get(deviceId);
        if (scanner) {
          scanner.isConnected = false;
        }
        this.debugLog('[HardwareService] Emitting scanner-disconnected for:', deviceId);
        this.emit('scanner-disconnected', deviceId);
      }
    } else {
      this.debugWarn('[HardwareService] Device not found for disconnection:', deviceId);
    }
  }

  /**
   * Handle UHB scale data
   */
  private handleUHBScaleData(data: any): void {
    // Handle both direct weight data and nested weight object
    const weightData = data.weight || data;

    // Format weight value to 2 decimal places to avoid floating point precision issues
    const rawValue = weightData.value || 0;
    const formattedValue = Math.round(rawValue * 100) / 100;

    const weight: Weight = {
      value: formattedValue,
      unit: weightData.unit || 'lbs',
      isStable: weightData.isStable !== undefined ? weightData.isStable : true,
      isEmpty: formattedValue === 0,
      timestamp: data.timestamp || Date.now()
    };

    // Cache the last known weight so it can be retrieved later
    this.lastKnownWeight = weight;

    this.emit('weight', {
      scaleId: data.deviceId || data.scaleId || 'default',
      weight: weight
    });
  }

  /**
   * Get the last known weight from cache (if available)
   * This is useful when opening a dialog and wanting to show the current weight
   * without waiting for a weight change event
   */
  getLastKnownWeight(): Weight | null {
    return this.lastKnownWeight;
  }

  /**
   * Override scale methods for UHB
   */
  async discoverScalesUHB(): Promise<Scale[]> {
    if (!this.config.useUHB) {
      return this.discoverScales();
    }

    this.debugLog('[HardwareService] Discovering scales in UHB mode');
    // Request device list via WebSocket (same as other discover methods)
    this.sendUHBMessage({ type: 'device.list' });

    // Wait for the device list to be received and processed
    await new Promise(resolve => setTimeout(resolve, 500));

    // Return current scales from cache
    const scales = Array.from(this.scales.values());
    this.debugLog('[HardwareService] Returning cached scales (device list already received):', scales.length);
    return scales;
  }

  /**
   * Override connect to scale for UHB
   */
  async connectToScaleUHB(scaleId: string): Promise<void> {
    if (!this.config.useUHB) {
      return this.connectToScale(scaleId);
    }

    const scale = this.scales.get(scaleId);

    // Check if scale is already connected
    if (scale?.isConnected) {
      this.debugLog('[HardwareService] Scale already connected, requesting current weight:', scaleId);
      // Still emit the event for UI consistency
      this.emit('scale-connected', scale);

      // Send subscribe request to trigger UHB to emit current cached weight
      // UHB will detect it's already subscribed and emit the current reading
      this.sendUHBMessage({
        type: 'scale.subscribe',
        data: { deviceId: scaleId }
      });

      return;
    }

    this.debugLog('[HardwareService] Connecting to scale:', scaleId);

    // Optimistically update the scale status
    if (scale) {
      scale.isConnected = true;
      // Emit scale-connected event immediately for UI update
      this.emit('scale-connected', scale);
    }

    this.sendUHBMessage({
      type: 'device.connect',
      data: { deviceId: scaleId }
    });

    // Request initial reading then subscribe
    setTimeout(() => {
      // First, request a single read to "wake up" the scale
      this.debugLog('[HardwareService] Requesting initial weight reading to wake up scale:', scaleId);
      this.sendUHBMessage({
        type: 'scale.read',
        data: { deviceId: scaleId }
      });

      // Then subscribe for continuous updates
      setTimeout(() => {
        this.debugLog('[HardwareService] Subscribing to scale updates:', scaleId);
        this.sendUHBMessage({
          type: 'scale.subscribe',
          data: { deviceId: scaleId }
        });
      }, 200);
    }, 500);
  }

  /**
   * Override get weight for UHB
   */
  async getWeightUHB(scaleId: string): Promise<Weight> {
    if (!this.config.useUHB) {
      return this.getWeight(scaleId);
    }

    // RATE LIMITING: Prevent rapid-fire weight requests
    const now = Date.now();
    const lastRequestTime = this.lastWeightRequestTime || 0;
    if (now - lastRequestTime < 100) { // Minimum 100ms between requests
      this.debugLog('[HardwareService] Rate limiting: Weight request too soon after previous');
      // Return last known weight if available
      const lastWeight = this.lastKnownWeight;
      if (lastWeight) {
        return lastWeight;
      }
    }
    this.lastWeightRequestTime = now;

    // Check if there's already a pending request for this scale
    const existingRequest = this.pendingWeightRequests.get(scaleId);
    if (existingRequest) {
      this.debugLog('[HardwareService] Reusing existing weight request for scale:', scaleId);
      return existingRequest;
    }

    // Create a new request
    const weightRequest = new Promise<Weight>((resolve, reject) => {
      let isResolved = false;

      // Generate unique message ID
      const messageId = `scale-read-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Define cleanup function that handles both handlers
      const cleanup = () => {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        this.off('uhb-response', responseHandler);
        this.off('weight', handler);
        // Remove from pending requests
        this.pendingWeightRequests.delete(scaleId);
      };

      let timeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          cleanup();
          reject(new Error('Timeout waiting for scale reading'));
        }
      }, 5000);

      // Handler for direct UHB response messages (with ID) - higher priority
      const responseHandler = (message: any) => {
        if (!isResolved && message.id === messageId && message.type === 'scale.read') {
          this.debugLog('[HardwareService] Received direct response for scale.read:', message);
          isResolved = true;
          cleanup();
          if (message.error) {
            reject(new Error(message.error.message || 'Scale read error'));
          } else {
            // Cache the last known weight
            this.lastKnownWeight = message.data;
            resolve(message.data);
          }
        }
      };

      // Fallback handler for broadcast weight messages (without ID)
      const handler = (data: any) => {
        if (!isResolved && data.scaleId === scaleId) {
          this.debugLog('[HardwareService] Received fallback weight broadcast for scale:', scaleId);
          isResolved = true;
          cleanup();
          // Cache the last known weight
          this.lastKnownWeight = data.weight;
          resolve(data.weight);
        }
      };

      // Listen for both response types
      this.on('uhb-response', responseHandler);
      this.on('weight', handler);

      // Request the weight reading with unique ID
      this.sendUHBMessage({
        id: messageId,
        type: 'scale.read',
        data: { deviceId: scaleId }
      });

      this.debugLog('[HardwareService] Sent scale.read request with ID:', messageId);
    });

    // Store the pending request
    this.pendingWeightRequests.set(scaleId, weightRequest);

    return weightRequest;
  }

  private hardwareError: string | null = null;

  /**
   * Connect to printer for UHB
   */
  async connectToPrinterUHB(printerId: string): Promise<void> {
    if (!this.config.useUHB) {
      return this.connectToPrinter(printerId);
    }
    
    this.debugLog('[HardwareService] Connecting to printer:', printerId);
    
    // For UHB printers, we don't need to "connect" in the traditional sense
    // Just mark it as connected since UHB handles the actual connection
    const printer = this.printers.get(printerId);
    if (printer) {
      printer.isConnected = true;
      // Emit printer-connected event for UI update
      this.emit('printer-connected', printer);
    }
    
    // Optionally send a connect message to UHB (though it may not be needed)
    this.sendUHBMessage({ 
      type: 'device.connect',
      data: { deviceId: printerId }
    });
  }

  /**
   * Disconnect from scale for UHB
   */
  async disconnectFromScaleUHB(scaleId: string): Promise<void> {
    if (!this.config.useUHB) {
      return this.disconnectFromScale(scaleId);
    }

    this.debugLog('[HardwareService] Disconnecting from scale:', scaleId);

    // Update the scale status
    const scale = this.scales.get(scaleId);
    if (scale) {
      scale.isConnected = false;
      // Emit scale-disconnected event immediately for UI update
      this.emit('scale-disconnected', scaleId);
    }

    const disconnectMessage = {
      type: 'device.disconnect',
      data: { deviceId: scaleId }
    };
    this.debugLog('[HardwareService] Sending UHB disconnect message for scale:', disconnectMessage);
    this.sendUHBMessage(disconnectMessage);
  }

  /**
   * Disconnect from printer for UHB
   */
  async disconnectFromPrinterUHB(printerId: string): Promise<void> {
    if (!this.config.useUHB) {
      return this.disconnectFromPrinter(printerId);
    }

    this.debugLog('[HardwareService] Disconnecting from printer:', printerId);

    // Update the printer status
    const printer = this.printers.get(printerId);
    if (printer) {
      printer.isConnected = false;
      // Emit printer-disconnected event for UI update
      this.emit('printer-disconnected', printerId);
    }

    const disconnectMessage = {
      type: 'device.disconnect',
      data: { deviceId: printerId }
    };
    this.debugLog('[HardwareService] Sending UHB disconnect message:', disconnectMessage);
    this.sendUHBMessage(disconnectMessage);
  }

  /**
   * Print PDF from URL for UHB
   * @param printerId - The ID of the printer to use
   * @param url - The URL of the PDF to print
   * @param options - Optional print settings (printAllPages, copies)
   */
  private async printLabelFromUrlUHB(
    printerId: string,
    url: string,
    options?: { printAllPages?: boolean; copies?: number }
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.config.useUHB) {
      throw new Error('Not in UHB mode');
    }

    this.debugLog('[HardwareService] printLabelFromUrlUHB called:', { printerId, url, options });

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout waiting for print response'));
      }, 120000); // 120 second timeout for PDF download and print

      // Generate unique message ID
      const messageId = `print-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Set up one-time response handler
      const handler = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data);
          if (message.id === messageId) {
            clearTimeout(timeout);

            // Remove the handler
            if (this.websocket) {
              this.websocket.removeEventListener('message', handler);
            }

            this.debugLog('[HardwareService] Print URL response:', message);

            if (message.error) {
              resolve({
                success: false,
                error: message.error.message || 'Print failed'
              });
            } else {
              resolve(message.data || { success: true });
            }
          }
        } catch (error) {
          // Ignore parse errors for messages that aren't for us
        }
      };

      // Add the handler
      if (this.websocket) {
        this.websocket.addEventListener('message', handler);
      }

      // Build print message with optional settings
      const printData: any = {
        deviceId: printerId,
        url: url,
        // Always request all pages to be printed (default behavior should be all pages anyway)
        printAllPages: options?.printAllPages !== false, // Default to true
      };

      if (options?.copies && options.copies > 1) {
        printData.copies = options.copies;
      }

      this.debugLog('[HardwareService] Sending printer.printUrl message:', { id: messageId, data: printData });

      // Send print URL message
      this.sendUHBMessage({
        id: messageId,
        type: 'printer.printUrl',
        data: printData
      });
    });
  }

  /**
   * Handle UHB scanner data
   */
  private handleUHBScannerData(data: any): void {
    this.debugLog('[HardwareService] handleUHBScannerData called with:', JSON.stringify(data));

    // CRITICAL FIX: Route ALL scan data to ScannerEventService FIRST
    // This ensures proper package QR detection happens before any local processing

    // Check if this is a keyboard-wedge-global event from UHB first
    if (data && data.type === 'keyboard-wedge-global') {
      this.debugLog('[HardwareService] ✅ Processing keyboard-wedge-global event:', data);

      // Preserve the original UHB event structure completely
      this.lastScannedData = data.data;

      // Route to ScannerEventService first - this is the critical fix
      this.debugLog('[HardwareService] ✅ CRITICAL FIX: Routing keyboard-wedge-global to ScannerEventService first');
      this.emit('scan', data); // Primary event - ScannerEventService listens to this for package QR detection

      this.debugLog('[HardwareService] ✅ Successfully routed keyboard-wedge-global to ScannerEventService');
      return; // Early return to prevent duplicate processing
    }

    // Check for any nested keyboard-wedge-global events
    if (data && data.data && data.data.type === 'keyboard-wedge-global') {
      this.debugLog('[HardwareService] ✅ Found nested keyboard-wedge-global event:', data.data);

      // Use the nested event structure
      const globalEvent = data.data;
      this.lastScannedData = globalEvent.data;

      // Route to ScannerEventService first
      this.debugLog('[HardwareService] ✅ CRITICAL FIX: Routing nested keyboard-wedge-global to ScannerEventService first');
      this.emit('scan', globalEvent); // Primary event for ScannerEventService

      this.debugLog('[HardwareService] ✅ Successfully routed nested keyboard-wedge-global to ScannerEventService');
      return; // Early return to prevent duplicate processing
    }

    // Handle regular scanner data - route to ScannerEventService FIRST
    this.debugLog('[HardwareService] Processing regular scanner data (not keyboard-wedge-global)');

    const scanData = data?.value || data?.data || data?.barcode || data?.qrcode || data?.code ||
                    (typeof data === 'string' ? data : null);
    const scannerId = data?.deviceId || data?.scannerId || 'default';

    this.debugLog('[HardwareService] Extracted regular scan data:', scanData, 'from scanner:', scannerId);

    if (scanData) {
      this.lastScannedData = scanData;

      // CRITICAL FIX: Route to ScannerEventService FIRST for proper package QR detection
      this.debugLog('[HardwareService] ✅ CRITICAL FIX: Routing regular scan data to ScannerEventService FIRST:', scanData);
      this.emit('scan', scanData); // PRIMARY: ScannerEventService handles package QR detection here

      // Also emit legacy events for backward compatibility
      this.emit('scanner.barcode', scanData);
      this.emit('scanner-data', scanData);
      this.emit('barcode', scanData);
      this.emit('scanner.data', scanData);

      this.debugLog('[HardwareService] ✅ Successfully routed scan data to ScannerEventService and emitted legacy events');

      // REMOVED: Local package QR processing - let ScannerEventService handle this
      // The ScannerEventService.extractPackageQRData() method is more robust and handles all the edge cases
    }
  }

  /**
   * Handle UHB scanner connected event
   */
  private handleUHBScannerConnected(data: any): void {
    this.debugLog('[HardwareService] Scanner connected event received:', data);
    
    const scannerId = data.deviceId || data.scannerId || data;
    const scanner = this.scanners.get(scannerId);
    
    if (scanner) {
      scanner.isConnected = true;
      this.debugLog('[HardwareService] Scanner marked as connected:', scannerId);
      this.emit('scanner-connected', scanner);
      
      // Auto-subscribe to scanner events
      setTimeout(() => {
        this.debugLog('[HardwareService] Auto-subscribing to scanner events:', scannerId);
        this.sendUHBMessage({
          type: 'scanner.subscribe',
          data: { deviceId: scannerId }
        });
      }, 500);
    } else {
      this.debugWarn('[HardwareService] Scanner not found for connection event:', scannerId);
    }
  }

  /**
   * Handle UHB scanner disconnected event
   */
  private handleUHBScannerDisconnected(deviceId: string): void {
    const scanner = this.scanners.get(deviceId);
    if (scanner) {
      scanner.isConnected = false;
      this.emit('scanner-disconnected', deviceId);
    }
  }

  /**
   * Diagnostic method - call from browser console:
   *   getHardwareService().diagnose()
   */
  diagnose(): void {
    const wsState = this.websocket?.readyState;
    const wsStateLabel = wsState === WebSocket.OPEN ? 'OPEN' :
                         wsState === WebSocket.CONNECTING ? 'CONNECTING' :
                         wsState === WebSocket.CLOSING ? 'CLOSING' :
                         wsState === WebSocket.CLOSED ? 'CLOSED' : 'NO SOCKET';

    console.group('[HardwareService] Diagnostic Report');
    console.log('UHB Mode:', this.config.useUHB);
    console.log('Server URL:', this.config.serverUrl);
    console.log('WebSocket State:', wsStateLabel, `(${wsState})`);
    console.log('Is Connected:', this.isConnected);
    console.log('Is Initialized:', this.isInitialized);
    console.log('Device List Received:', this.deviceListReceived);
    console.log('Scales:', Array.from(this.scales.values()).map(s => ({ id: s.id, connected: s.isConnected })));
    console.log('Printers:', Array.from(this.printers.values()).map(p => ({ id: p.id, name: p.name, connected: p.isConnected })));
    console.log('Scanners:', Array.from(this.scanners.values()).map(s => ({ id: s.id, name: s.name, connected: s.isConnected })));
    console.log('Event Listener Counts:', {
      scan: this.listenerCount('scan'),
      barcode: this.listenerCount('barcode'),
      'scanner.barcode': this.listenerCount('scanner.barcode'),
      connected: this.listenerCount('connected'),
      disconnected: this.listenerCount('disconnected')
    });
    console.groupEnd();

    // Send a ping to verify UHB is responsive
    if (this.websocket?.readyState === WebSocket.OPEN) {
      console.log('[HardwareService] Sending device.list ping to UHB...');
      this.sendUHBMessage({ type: 'device.list' });
    } else {
      console.warn('[HardwareService] Cannot ping UHB - WebSocket not open. Attempting reconnect...');
      this.connectToUHB();
    }
  }

  /**
   * Send a test barcode through UHB's scanner.manualInput path.
   * This bypasses GlobalKeyboardScanner and tests the WebSocket broadcast chain.
   * Call from browser console: window.__hardwareService.testScanBroadcast()
   * If the test barcode arrives back, the broadcast chain works and the issue is
   * GlobalKeyboardScanner not capturing physical scanner input.
   */
  testScanBroadcast(testBarcode?: string): void {
    const barcode = testBarcode || '99999'; // Use a fake pick ticket ID
    console.log('[HardwareService] === SCANNER BROADCAST TEST ===');
    console.log('[HardwareService] Sending test barcode via scanner.manualInput:', barcode);
    console.log('[HardwareService] If you see a scanner event come back, the WebSocket broadcast works.');
    console.log('[HardwareService] If nothing comes back, UHB is not broadcasting scanner events.');

    if (this.websocket?.readyState !== WebSocket.OPEN) {
      console.error('[HardwareService] Cannot test - WebSocket is not open');
      return;
    }

    this.sendUHBMessage({
      type: 'scanner.manualInput',
      data: { scanData: barcode }
    });
  }
}

// Singleton instance
let hardwareService: HardwareIntegrationService | null = null;

export function getHardwareService(forceUHB?: boolean): HardwareIntegrationService {
  if (!hardwareService) {
    // Force UHB if specified
    const config: HardwareServiceConfig = {};
    if (forceUHB !== undefined) {
      config.useUHB = forceUHB;
    }
    hardwareService = new HardwareIntegrationService(config);
  }
  // Expose on window for console diagnostics: window.__hardwareService.diagnose()
  if (typeof window !== 'undefined') {
    (window as any).__hardwareService = hardwareService;
  }
  return hardwareService;
}

/**
 * Destroy the hardware service singleton and close all connections.
 * Call on logout to prevent the previous user's WebSocket from persisting.
 */
export function resetHardwareService(): void {
  if (hardwareService) {
    hardwareService.destroy();
    hardwareService = null;
  }
}